/**
 * Patologías List Component
 *
 * Lista de patologías/historial clínico de un paciente.
 * Incluye acciones para crear, editar, eliminar y activar/desactivar.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  Plus,
  Pencil,
  Trash2,
  Activity,
  ActivitySquare,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

import {
  usePatologias,
  useDeletePatologia,
  useDeactivatePatologia,
  useActivatePatologia,
  type PatologiaListItem,
} from "@/hooks/use-patologias";
import { useApiError } from "@/hooks/use-api-error";
import { PatologiaFormDialog } from "./patologia-form-dialog";

interface PatologiasListProps {
  pacienteId: number;
  pacienteNombre: string;
}

export function PatologiasList({ pacienteId, pacienteNombre }: PatologiasListProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();

  const [showFormDialog, setShowFormDialog] = useState(false);
  const [editingPatologia, setEditingPatologia] = useState<PatologiaListItem | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch patologías for this patient
  const { data, isLoading, refetch } = usePatologias({
    paciente: pacienteId,
    page_size: 100,
    ordering: "-fecha_registro",
  });

  const deleteMutation = useDeletePatologia({
    onSuccess: () => {
      setDeleteId(null);
      refetch();
    },
    onError: (err) => setError(getErrorMessage(err)),
  });

  const deactivateMutation = useDeactivatePatologia({
    onSuccess: () => refetch(),
    onError: (err) => setError(getErrorMessage(err)),
  });

  const activateMutation = useActivatePatologia({
    onSuccess: () => refetch(),
    onError: (err) => setError(getErrorMessage(err)),
  });

  const handleDelete = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
    }
  };

  const handleEdit = (patologia: PatologiaListItem) => {
    setEditingPatologia(patologia);
    setShowFormDialog(true);
  };

  const handleCreate = () => {
    setEditingPatologia(null);
    setShowFormDialog(true);
  };

  const handleToggleActive = (patologia: PatologiaListItem) => {
    if (patologia.is_active) {
      deactivateMutation.mutate(patologia.id);
    } else {
      activateMutation.mutate(patologia.id);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "dd/MM/yyyy", { locale: es });
    } catch {
      return dateString;
    }
  };

  const patologias = data?.results || [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div>
          <CardTitle className="text-lg">
            {t("patologias.title", "Patologías / Historial Clínico")}
          </CardTitle>
          <CardDescription>
            {t("patologias.description", "Enfermedades y condiciones del paciente")}
          </CardDescription>
        </div>
        <Button onClick={handleCreate} size="sm">
          <Plus className="h-4 w-4 mr-1" />
          {t("patologias.new", "Nueva")}
        </Button>
      </CardHeader>

      <CardContent>
        {error && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive mb-4 flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : patologias.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <ActivitySquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>{t("patologias.noPatologias", "No hay patologías registradas")}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("patologias.fields.name", "Nombre")}</TableHead>
                <TableHead>{t("patologias.fields.date", "Fecha Registro")}</TableHead>
                <TableHead>{t("patologias.fields.active", "Estado Clínico")}</TableHead>
                <TableHead>{t("patologias.fields.status", "Estado")}</TableHead>
                <TableHead className="text-right">{t("common.actions", "Acciones")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {patologias.map((item) => (
                <TableRow key={item.id} className={!item.is_active ? "opacity-60" : ""}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Activity
                        className={`h-4 w-4 ${item.activa ? "text-red-500" : "text-muted-foreground"}`}
                      />
                      <div>
                        <span className="font-medium">{item.nombre}</span>
                        {item.notas && (
                          <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                            {item.notas}
                          </p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(item.fecha_registro)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={item.activa ? "destructive" : "secondary"}>
                      {item.activa
                        ? t("patologias.activeCondition", "Activa")
                        : t("patologias.resolvedCondition", "Resuelta")}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={item.is_active ? "default" : "outline"}>
                      {item.is_active ? t("common.active") : t("common.inactive")}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <TooltipProvider>
                      <div className="flex items-center justify-end gap-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant="ghost" size="icon" onClick={() => handleEdit(item)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>{t("common.edit", "Editar")}</TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleToggleActive(item)}
                              disabled={deactivateMutation.isPending || activateMutation.isPending}
                            >
                              {item.is_active ? (
                                <XCircle className="h-4 w-4 text-orange-500" />
                              ) : (
                                <CheckCircle className="h-4 w-4 text-green-500" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {item.is_active ? t("common.deactivate") : t("common.activate")}
                          </TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeleteId(item.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>{t("common.delete", "Eliminar")}</TooltipContent>
                        </Tooltip>
                      </div>
                    </TooltipProvider>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Form Dialog */}
      <PatologiaFormDialog
        open={showFormDialog}
        onOpenChange={setShowFormDialog}
        pacienteId={pacienteId}
        pacienteNombre={pacienteNombre}
        patologia={editingPatologia}
        onSuccess={() => {
          setShowFormDialog(false);
          setEditingPatologia(null);
          refetch();
        }}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("patologias.deleteTitle", "¿Eliminar patología?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "patologias.deleteDescription",
                "Esta acción no se puede deshacer. La patología será eliminada permanentemente del historial."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("common.delete", "Eliminar")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
